<template>
  <div class="note-page">
    <!-- ── 页头：标题 + 审核状态分段筛选 ─────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>纸条管理</h1>
        <p>校园匿名纸条的投放、审核与取出记录</p>
      </div>
      <div class="status-tabs">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          type="button"
          :class="['tab', { 'is-active': listQuery.status === tab.value }]"
          @click="switchStatus(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.keyword"
        placeholder="搜索纸条内容"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select
        v-model="listQuery.schoolId"
        placeholder="全部学校"
        clearable
        class="school-select"
        @change="handleFilter"
      >
        <el-option v-for="item in schools" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">
        共 <b>{{ total }}</b> 张
      </div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table
        v-loading="listLoading"
        :data="list"
        class="note-table"
        :row-class-name="rowClass"
        @row-click="handleDetail"
      >
        <el-table-column label="纸条内容" min-width="300">
          <template slot-scope="scope">
            <div class="note-cell">
              <p class="note-text">{{ scope.row.message }}</p>
              <div class="note-tags">
                <span v-if="scope.row.contactInfo" class="tag tag-contact">
                  <i class="el-icon-phone-outline" />{{ scope.row.contactInfo }}
                </span>
                <span v-else class="tag tag-muted">未留联系方式</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="发布者" width="176">
          <template slot-scope="scope">
            <div class="owner-cell">
              <el-avatar :size="30" :src="scope.row.ownerAvatar" class="owner-avatar">
                {{ (scope.row.ownerNickname || '?').slice(0, 1) }}
              </el-avatar>
              <div class="owner-info">
                <div class="owner-name">
                  {{ scope.row.ownerNickname || '用户' + scope.row.userId }}
                  <span v-if="scope.row.ownerVerified" class="verify-dot" title="已认证">
                    <i class="el-icon-check" />
                  </span>
                </div>
                <div class="owner-school">{{ schoolName(scope.row.schoolId) }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="取出进度" width="132">
          <template slot-scope="scope">
            <div class="take-cell">
              <div class="take-num">
                <b>{{ scope.row.currentTakeCount || 0 }}</b>
                <span>/ {{ scope.row.maxTakeCount }}</span>
                <em v-if="isFull(scope.row)">已取完</em>
              </div>
              <div class="take-bar">
                <i :style="{ width: takePercent(scope.row) + '%' }" />
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="投放费用" width="110" align="right">
          <template slot-scope="scope">
            <div v-if="Number(scope.row.fee) > 0" class="fee-val">
              {{ beanNumber(scope.row.fee) }}<span>爱豆</span>
            </div>
            <div v-else class="fee-val is-free">免费</div>
          </template>
        </el-table-column>

        <el-table-column label="审核状态" width="120">
          <template slot-scope="scope">
            <span class="status-pill" :class="'s' + scope.row.status">
              <i />
              {{ statusText(scope.row.status) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="投放时间" width="132">
          <template slot-scope="scope">
            <div class="time-cell">
              <div>{{ formatDate(scope.row.createdAt) }}</div>
              <div class="time-hm">{{ formatTime(scope.row.createdAt) }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="168" fixed="right" align="right">
          <template slot-scope="scope">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click.native.stop="handleDetail(scope.row)">
                详情
              </button>
              <button
                v-if="scope.row.status === 0"
                type="button"
                class="act act-primary"
                @click.native.stop="handleReview(scope.row)"
              >
                审核
              </button>
              <el-dropdown
                v-else
                trigger="click"
                placement="bottom-end"
                @command="cmd => handleCommand(cmd, scope.row)"
              >
                <button type="button" class="act act-plain act-more" @click.stop>
                  更多<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="edit">
                    <i class="el-icon-edit-outline" /> 编辑纸条
                  </el-dropdown-item>
                  <el-dropdown-item command="review">
                    <i class="el-icon-refresh" /> 重新审核
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>
                    <i class="el-icon-delete" /> 删除纸条
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="empty-state">
            <i class="el-icon-document" />
            <p>{{ emptyText }}</p>
          </div>
        </template>
      </el-table>

      <el-pagination
        v-show="total > 0"
        :current-page="listQuery.page"
        :page-size="listQuery.size"
        :page-sizes="[10, 20, 50]"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- ── 详情抽屉 ───────────────────────────────────────── -->
    <el-drawer
      :visible.sync="detailVisible"
      direction="rtl"
      size="640px"
      :with-header="false"
      custom-class="note-drawer"
      @closed="onDrawerClosed"
    >
      <div v-if="noteDetail.id" class="drawer-inner">
        <div class="drawer-head">
          <div class="dh-left">
            <span class="dh-id">纸条 #{{ noteDetail.id }}</span>
            <span class="status-pill" :class="'s' + noteDetail.status">
              <i />
              {{ statusText(noteDetail.status) }}
            </span>
          </div>
          <button type="button" class="dh-close" @click="detailVisible = false">
            <i class="el-icon-close" />
          </button>
        </div>

        <div class="drawer-body">
          <!-- 纸条本体：详情里的视觉重心 -->
          <div class="paper">
            <div class="paper-fold" />
            <p class="paper-text">{{ noteDetail.message }}</p>
            <div class="paper-sign">
              <span class="paper-sign-line" />
              <span>{{ noteDetail.ownerNickname || '用户' + noteDetail.userId }}</span>
              <span class="paper-sign-school">{{ schoolName(noteDetail.schoolId) }}</span>
            </div>
            <div v-if="noteDetail.contactInfo" class="paper-contact">
              联系方式：<b>{{ noteDetail.contactInfo }}</b>
            </div>
          </div>

          <!-- 关键指标 -->
          <div class="metric-row">
            <div class="metric">
              <b>{{ noteDetail.currentTakeCount || 0 }}<em>/ {{ noteDetail.maxTakeCount }}</em></b>
              <span>取出进度</span>
            </div>
            <div class="metric">
              <b v-if="Number(noteDetail.fee) > 0">{{ beanNumber(noteDetail.fee) }}<em>爱豆</em></b>
              <b v-else>免费</b>
              <span>投放费用</span>
            </div>
            <div class="metric">
              <b>{{ takeRecords.length }}</b>
              <span>取出记录</span>
            </div>
          </div>

          <!-- 基础信息 -->
          <div class="section">
            <div class="section-title">基础信息</div>
            <div class="info-grid">
              <div class="info-item">
                <label>纸条 ID</label>
                <span>{{ noteDetail.id }}</span>
              </div>
              <div class="info-item">
                <label>发布者</label>
                <span>{{ noteDetail.ownerNickname || '用户' + noteDetail.userId }}（ID {{ noteDetail.userId }}）</span>
              </div>
              <div class="info-item">
                <label>所属学校</label>
                <span>{{ schoolName(noteDetail.schoolId) }}</span>
              </div>
              <div class="info-item">
                <label>投放时间</label>
                <span>{{ formatDateTime(noteDetail.createdAt) }}</span>
              </div>
            </div>
          </div>

          <!-- 审核信息 -->
          <div class="section">
            <div class="section-title">审核信息</div>
            <div class="info-grid">
              <div class="info-item">
                <label>审核状态</label>
                <span>{{ statusText(noteDetail.status) }}</span>
              </div>
              <div class="info-item">
                <label>审核时间</label>
                <span>{{ noteDetail.reviewedAt ? formatDateTime(noteDetail.reviewedAt) : '—' }}</span>
              </div>
              <div class="info-item is-full">
                <label>审核备注</label>
                <span :class="{ 'is-muted': !noteDetail.reviewRemark }">
                  {{ noteDetail.reviewRemark || '无' }}
                </span>
              </div>
            </div>
            <div v-if="noteDetail.status === 0" class="review-hint">
              这张纸条还在等待审核，通过后才会出现在小程序的纸条池里。
            </div>
          </div>

          <!-- 取出记录 -->
          <div class="section">
            <div class="section-title">
              取出记录
              <span class="count">{{ takeRecords.length }}</span>
            </div>
            <div v-if="takeLoading" class="take-loading">加载中…</div>
            <ul v-else-if="takeRecords.length" class="take-list">
              <li v-for="item in takeRecords" :key="item.id">
                <span class="take-dot" />
                <div class="take-main">
                  <b>{{ item.takerNickname || '用户' + item.takerUserId }}</b>
                  <span class="take-time">{{ formatDateTime(item.createdAt) }}</span>
                </div>
              </li>
            </ul>
            <div v-else class="take-empty">还没有人取走这张纸条</div>
          </div>
        </div>

        <div class="drawer-foot">
          <el-button size="small" class="ghost-btn" @click="handleEdit(noteDetail)">编辑</el-button>
          <el-button size="small" class="danger-btn" @click="handleDelete(noteDetail)">删除</el-button>
          <div class="foot-spacer" />
          <template v-if="noteDetail.status === 0">
            <el-button size="small" type="danger" plain @click="handleReview(noteDetail, 'reject')">
              驳回
            </el-button>
            <el-button size="small" type="primary" @click="handleReview(noteDetail, 'approve')">
              审核通过
            </el-button>
          </template>
          <template v-else>
            <el-button size="small" class="ghost-btn" @click="handleReview(noteDetail)">
              重新审核
            </el-button>
          </template>
        </div>
      </div>
    </el-drawer>

    <!-- ── 编辑弹窗 ───────────────────────────────────────── -->
    <el-dialog title="编辑纸条" :visible.sync="dialogVisible" width="520px">
      <el-form ref="form" :model="form" :rules="rules" label-width="96px">
        <el-form-item label="纸条内容" prop="message">
          <el-input v-model="form.message" type="textarea" :rows="4" maxlength="500" show-word-limit />
        </el-form-item>
        <el-form-item label="联系方式" prop="contactInfo">
          <el-input v-model="form.contactInfo" />
        </el-form-item>
        <el-form-item label="最大取出次数" prop="maxTakeCount">
          <el-input-number v-model="form.maxTakeCount" :min="1" :max="10" />
          <span class="form-tip">不能小于已被取出的次数</span>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>

    <!-- ── 审核弹窗 ───────────────────────────────────────── -->
    <el-dialog title="审核纸条" :visible.sync="reviewDialogVisible" width="520px">
      <div v-if="currentNote.message" class="review-preview">
        <p>{{ currentNote.message }}</p>
      </div>
      <el-form label-width="96px">
        <el-form-item label="审核结果">
          <el-radio-group v-model="reviewForm.action">
            <el-radio label="approve">通过</el-radio>
            <el-radio label="reject">不通过</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="审核备注">
          <el-input
            v-model="reviewForm.remark"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            :placeholder="reviewForm.action === 'reject' ? '建议说明驳回原因，会通知到发布者' : '可选'"
          />
        </el-form-item>
      </el-form>
      <div v-if="reviewForm.action === 'reject'" class="refund-tip">
        驳回后，纸条不再展示；已支付的投放费用会自动退回发布者的爱豆账户。
      </div>
      <span slot="footer">
        <el-button @click="reviewDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitReview">提交审核</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getNoteList, getNoteDetail, updateNote, deleteNote, getNoteTakeRecords, approveNote, rejectNote } from '@/api/note'
import { getSchoolOptions } from '@/api/school'
import { beanNumber } from '@/utils/bean'

const STATUS_TABS = [
  { label: '全部', value: '' },
  { label: '待审核', value: '0' },
  { label: '已通过', value: '1' },
  { label: '已驳回', value: '2' }
]

const STATUS_TEXT = { 0: '待审核', 1: '已通过', 2: '已驳回' }

export default {
  name: 'Note',
  data() {
    return {
      statusTabs: STATUS_TABS,
      list: [],
      total: 0,
      listLoading: false,
      schools: [],
      listQuery: {
        page: 1,
        size: 10,
        keyword: '',
        status: '',
        schoolId: ''
      },
      dialogVisible: false,
      reviewDialogVisible: false,
      currentNote: {},
      reviewForm: {
        action: 'approve',
        remark: ''
      },
      form: {
        id: null,
        message: '',
        contactInfo: '',
        maxTakeCount: 1
      },
      rules: {
        message: [{ required: true, message: '请输入纸条内容', trigger: 'blur' }],
        contactInfo: [{ required: true, message: '请输入联系方式', trigger: 'blur' }]
      },
      detailVisible: false,
      noteDetail: {},
      takeRecords: [],
      takeLoading: false
    }
  },
  computed: {
    emptyText() {
      if (this.listQuery.keyword) {
        return '没有匹配「' + this.listQuery.keyword + '」的纸条'
      }
      if (this.listQuery.status === '0') {
        return '太好了，没有待审核的纸条'
      }
      return '暂时还没有纸条'
    }
  },
  created() {
    // 深链：钉钉消息里带 ?status=0&focus=<纸条ID>，点进来直接开那张纸条的抽屉。
    // ⛔ 不做这一步，通知里的链接点进去只是个列表，运营还得自己翻页找 —— 通知就废了一半。
    const { status, focus } = this.$route.query
    if (status !== undefined && status !== null && status !== '') {
      this.listQuery.status = String(status)
    }
    this.fetchData()
    this.fetchSchools()
    this.applyFocus(focus)
  },
  methods: {
    beanNumber,
    /** 深链定位并清掉 query，避免刷新时重复弹同一个抽屉 */
    applyFocus(focus) {
      const id = Number(focus)
      if (!id || Number.isNaN(id)) return
      this.handleDetail({ id })
      const query = { ...this.$route.query }
      delete query.focus
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    fetchSchools() {
      // 非超管只会拿到自己被授权的学校，所以这个筛选器不会出现"别人的学校"
      getSchoolOptions().then(res => {
        this.schools = res.data || []
      }).catch(() => {
        this.schools = []
      })
    },
    fetchData() {
      this.listLoading = true
      getNoteList(this.listQuery).then(res => {
        this.list = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0
        this.listLoading = false
      }).catch(() => {
        this.listLoading = false
      })
    },
    switchStatus(value) {
      if (this.listQuery.status === value) return
      this.listQuery.status = value
      this.listQuery.page = 1
      this.fetchData()
    },
    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    resetFilter() {
      this.listQuery.keyword = ''
      this.listQuery.schoolId = ''
      this.listQuery.status = ''
      this.listQuery.page = 1
      this.fetchData()
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.listQuery.page = 1
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    handleDetail(row) {
      this.noteDetail = {}
      this.takeRecords = []
      this.detailVisible = true
      getNoteDetail(row.id).then(res => {
        this.noteDetail = res.data || {}
        this.loadTakeRecords(row.id)
      })
    },
    loadTakeRecords(id) {
      this.takeLoading = true
      getNoteTakeRecords(id).then(res => {
        this.takeRecords = res.data || []
        this.takeLoading = false
      }).catch(() => {
        this.takeRecords = []
        this.takeLoading = false
      })
    },
    onDrawerClosed() {
      this.noteDetail = {}
      this.takeRecords = []
    },
    handleCommand(command, row) {
      if (command === 'edit') {
        this.handleEdit(row)
      } else if (command === 'review') {
        this.handleReview(row)
      } else if (command === 'delete') {
        this.handleDelete(row)
      }
    },
    handleEdit(row) {
      this.form = {
        id: row.id,
        message: row.message,
        contactInfo: row.contactInfo,
        maxTakeCount: row.maxTakeCount
      }
      this.dialogVisible = true
    },
    handleDelete(row) {
      const label = '纸条 #' + row.id
      this.$confirm('删除后无法恢复，' + label + ' 的取出记录也会一并失效。确定删除吗？', '删除纸条', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        deleteNote(row.id).then(() => {
          this.$message.success('已删除')
          if (this.detailVisible && this.noteDetail.id === row.id) {
            this.detailVisible = false
          }
          this.fetchData()
        })
      }).catch(() => {})
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          updateNote(this.form).then(() => {
            this.$message.success('修改成功')
            this.dialogVisible = false
            if (this.detailVisible && this.noteDetail.id === this.form.id) {
              this.handleDetail({ id: this.form.id })
            }
            this.fetchData()
          })
        }
      })
    },
    handleReview(row, action) {
      this.currentNote = { ...row }
      this.reviewForm = {
        action: action || 'approve',
        remark: ''
      }
      this.reviewDialogVisible = true
    },
    submitReview() {
      const { id } = this.currentNote
      const { action, remark } = this.reviewForm
      const api = action === 'approve' ? approveNote : rejectNote
      const okText = action === 'approve' ? '审核通过' : '已驳回'

      api({ id, remark }).then(res => {
        if (res.code === 200) {
          this.$message.success(okText)
          this.reviewDialogVisible = false
          if (this.detailVisible && this.noteDetail.id === id) {
            this.detailVisible = false
          }
          this.fetchData()
        } else {
          this.$message.error(res.message || '审核失败')
        }
      }).catch(() => {
        this.$message.error('审核失败')
      })
    },

    /* ── 展示辅助 ─────────────────────────────────────── */
    statusText(status) {
      return STATUS_TEXT[status] || '未知'
    },
    isFull(row) {
      return (row.currentTakeCount || 0) >= row.maxTakeCount
    },
    takePercent(row) {
      if (!row.maxTakeCount) return 0
      const p = ((row.currentTakeCount || 0) / row.maxTakeCount) * 100
      return Math.min(100, Math.round(p))
    },
    schoolName(schoolId) {
      const hit = this.schools.find(item => item.id === schoolId)
      return hit ? hit.name : '学校 #' + (schoolId || '—')
    },
    rowClass() {
      return ''
    },
    formatDate(date) {
      if (!date) return '—'
      return String(date).slice(0, 10)
    },
    formatTime(date) {
      if (!date) return ''
      return String(date).slice(11, 16)
    },
    formatDateTime(date) {
      if (!date) return '—'
      return String(date).slice(0, 16)
    }
  }
}
</script>

<style lang="scss">
/* 纸条模块专用视觉 —— 复用小程序端的玫瑰 / 墨色体系，与 element-ui 默认蓝做隔离 */
.note-page {
  --ink-900: #171219;
  --ink-700: #3a2f3f;
  --ink-500: #6b5f70;
  --ink-400: #8c8191;
  --ink-300: #c7bfc9;
  --ink-200: #e8e0e6;
  --ink-100: #f4eff2;
  --rose-700: #8e2239;
  --rose-600: #a82b44;
  --rose-500: #c93b57;
  --rose-400: #e14d68;
  --rose-100: #fbe0e4;
  --rose-50: #fdf1f0;
  --gold: #c9a227;
  --ok: #3f7a5e;
  --warn: #c98a1f;
  --paper: #fbf7f4;

  padding-bottom: 8px;
  color: var(--ink-900);

  /* ── 页头 ─────────────────────────────────────────── */
  .page-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    padding: 4px 2px 18px;
    flex-wrap: wrap;
  }

  .head-text {
    h1 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      letter-spacing: 0.5px;
      color: var(--ink-900);
    }

    p {
      margin: 6px 0 0;
      font-size: 13px;
      color: var(--ink-400);
    }
  }

  .status-tabs {
    display: inline-flex;
    padding: 3px;
    background: #fff;
    border: 1px solid var(--ink-200);
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(23, 18, 25, 0.04);
  }

  .status-tabs .tab {
    appearance: none;
    border: 0;
    background: transparent;
    padding: 7px 18px;
    font-size: 13px;
    color: var(--ink-500);
    border-radius: 7px;
    cursor: pointer;
    transition: all 0.18s;
    font-family: inherit;

    &:hover {
      color: var(--ink-900);
      background: var(--ink-100);
    }

    &.is-active {
      background: var(--rose-600);
      color: #fff;
      font-weight: 500;
      box-shadow: 0 2px 6px rgba(168, 43, 68, 0.25);
    }
  }

  /* ── 筛选条 ───────────────────────────────────────── */
  .filter-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid var(--ink-200);
    border-radius: 12px;
    margin-bottom: 14px;

    .search-input {
      width: 240px;
    }

    .school-select {
      width: 180px;
    }

    .primary-btn {
      background: var(--rose-600);
      border-color: var(--rose-600);
      padding: 10px 20px;

      &:hover,
      &:focus {
        background: var(--rose-500);
        border-color: var(--rose-500);
      }
    }

    .ghost-btn {
      color: var(--ink-500);
      border-color: var(--ink-200);
      padding: 10px 16px;

      &:hover {
        color: var(--rose-600);
        border-color: var(--rose-500);
        background: var(--rose-50);
      }
    }

    .filter-tail {
      margin-left: auto;
      font-size: 13px;
      color: var(--ink-400);

      b {
        color: var(--ink-900);
        font-size: 14px;
      }
    }
  }

  /* ── 表格 ─────────────────────────────────────────── */
  .table-card {
    background: #fff;
    border: 1px solid var(--ink-200);
    border-radius: 12px;
    padding: 4px 4px 0;
    overflow: hidden;
  }

  /* 注意：左右固定列是 el-table 的兄弟节点，选择器必须挂在 .table-card 上，
     挂在 .el-table 上会漏掉固定层，hover 时操作列不跟随高亮 */
  .table-card {
    .el-table {
      width: 100%;

      &::before {
        display: none;
      }
    }

    th {
      background: var(--paper) !important;
      border-bottom: 1px solid var(--ink-200) !important;
    }

    th .cell {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.6px;
      color: var(--ink-400);
    }

    td {
      border-bottom: 1px solid var(--ink-100) !important;
      padding: 14px 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background 0.15s;

      &:hover > td {
        background: var(--rose-50) !important;
      }

      &:hover .note-cell {
        border-left-color: var(--rose-500);
      }
    }

    .el-table__fixed-right::before,
    .el-table__fixed::before {
      display: none;
    }

    .el-table__fixed-right {
      box-shadow: -6px 0 14px -8px rgba(23, 18, 25, 0.18);
    }
  }

  /* 纸条内容 —— 一条玫瑰竖线，让"这是一张纸条"看得见 */
  .note-cell {
    border-left: 2px solid var(--rose-100);
    padding-left: 12px;
    transition: border-color 0.18s;
  }

  .note-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: var(--ink-900);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  .note-tags {
    margin-top: 8px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 5px;
    line-height: 18px;
  }

  .tag-contact {
    background: var(--ink-100);
    color: var(--ink-500);
    font-family: 'SFMono-Regular', Consolas, monospace;
  }

  .tag-muted {
    color: var(--ink-300);
    padding-left: 0;
  }

  /* 发布者 */
  .owner-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .owner-avatar {
    flex: 0 0 auto;
    background: var(--ink-100);
    color: var(--ink-500);
    font-size: 13px;
  }

  .owner-info {
    min-width: 0;
  }

  .owner-name {
    font-size: 13px;
    color: var(--ink-900);
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .verify-dot {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--gold);
    color: #fff;
    font-size: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .owner-school {
    margin-top: 2px;
    font-size: 12px;
    color: var(--ink-400);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 取出进度 */
  .take-num {
    display: flex;
    align-items: baseline;
    gap: 3px;
    font-size: 13px;
    color: var(--ink-400);

    b {
      font-size: 15px;
      color: var(--ink-900);
    }

    em {
      margin-left: auto;
      font-style: normal;
      font-size: 11px;
      color: var(--ink-400);
      background: var(--ink-100);
      border-radius: 4px;
      padding: 1px 6px;
    }
  }

  .take-bar {
    margin-top: 7px;
    height: 4px;
    border-radius: 4px;
    background: var(--ink-100);
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 4px;
      background: linear-gradient(90deg, var(--rose-500), var(--rose-400));
      transition: width 0.3s;
    }
  }

  /* 费用 */
  .fee-val {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink-900);

    span {
      margin-left: 3px;
      font-size: 11px;
      font-weight: 400;
      color: var(--ink-400);
    }

    &.is-free {
      font-size: 13px;
      font-weight: 400;
      color: var(--ink-400);
    }
  }

  /* 状态 */
  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--ink-700);
    white-space: nowrap;

    i {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex: 0 0 auto;
      background: var(--ink-300);
    }

    &.s0 i {
      background: var(--warn);
      box-shadow: 0 0 0 3px rgba(201, 138, 31, 0.15);
    }

    &.s1 i {
      background: var(--ok);
      box-shadow: 0 0 0 3px rgba(63, 122, 94, 0.15);
    }

    &.s2 i {
      background: var(--rose-500);
      box-shadow: 0 0 0 3px rgba(201, 59, 87, 0.15);
    }
  }

  /* 时间 */
  .time-cell {
    font-size: 13px;
    color: var(--ink-700);
    line-height: 1.5;

    .time-hm {
      font-size: 12px;
      color: var(--ink-400);
    }
  }

  /* 行内操作 */
  .row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  .act {
    appearance: none;
    font-family: inherit;
    font-size: 13px;
    padding: 5px 12px;
    border-radius: 7px;
    cursor: pointer;
    transition: all 0.16s;
    border: 1px solid transparent;
    line-height: 18px;
  }

  .act-plain {
    color: var(--ink-500);
    background: transparent;
    border-color: var(--ink-200);

    &:hover {
      color: var(--rose-600);
      border-color: var(--rose-500);
      background: var(--rose-50);
    }
  }

  .act-primary {
    color: #fff;
    background: var(--rose-600);

    &:hover {
      background: var(--rose-500);
    }
  }

  .act-more {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;

    i {
      font-size: 12px;
    }
  }

  /* 空态 */
  .empty-state {
    padding: 42px 0;

    i {
      font-size: 34px;
      color: var(--ink-300);
    }

    p {
      margin: 12px 0 0;
      font-size: 13px;
      color: var(--ink-400);
    }
  }

  /* 分页 */
  .el-pagination {
    margin: 0;
    padding: 14px 8px 12px;
    text-align: right;

    .el-pager li.active {
      color: var(--rose-600);
    }

    .el-pager li:hover {
      color: var(--rose-500);
    }
  }

  /* ── 把 element-ui 默认蓝收敛到玫瑰色（仅本页生效） ────── */
  .el-button--primary {
    background: var(--rose-600);
    border-color: var(--rose-600);

    &:hover,
    &:focus {
      background: var(--rose-500);
      border-color: var(--rose-500);
    }
  }

  .el-button--danger {
    background: var(--rose-600);
    border-color: var(--rose-600);

    &:hover,
    &:focus {
      background: var(--rose-500);
      border-color: var(--rose-500);
    }

    &.is-plain {
      color: var(--rose-600);
      background: var(--rose-50);
      border-color: #e9a3b0;

      &:hover,
      &:focus {
        color: #fff;
        background: var(--rose-600);
        border-color: var(--rose-600);
      }
    }
  }

  .el-input__inner:focus,
  .el-textarea__inner:focus {
    border-color: var(--rose-500);
  }

  .el-radio__input.is-checked .el-radio__inner {
    background: var(--rose-600);
    border-color: var(--rose-600);
  }

  .el-radio__input.is-checked + .el-radio__label,
  .el-radio__label:hover {
    color: var(--rose-600);
  }

  .el-select .el-input.is-focus .el-input__inner {
    border-color: var(--rose-500);
  }

  .el-dropdown-menu__item:hover {
    background: var(--rose-50);
    color: var(--rose-600);
  }

  /* 弹窗内的小件 */
  .form-tip {
    margin-left: 10px;
    font-size: 12px;
    color: var(--ink-400);
  }

  .review-preview {
    margin: 0 0 18px;
    padding: 14px 16px;
    background: var(--paper);
    border-left: 3px solid var(--rose-400);
    border-radius: 0 8px 8px 0;

    p {
      margin: 0;
      font-size: 13px;
      line-height: 1.7;
      color: var(--ink-700);
      max-height: 120px;
      overflow: auto;
    }
  }

  .refund-tip {
    margin-top: -6px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.6;
    color: #8e5b12;
    background: #fdf7e8;
    border-radius: 8px;
  }
}

/* ── 详情抽屉（挂在 body 上，独立命名空间） ──────────────── */
.note-drawer {
  --ink-900: #171219;
  --ink-700: #3a2f3f;
  --ink-500: #6b5f70;
  --ink-400: #8c8191;
  --ink-300: #c7bfc9;
  --ink-200: #e8e0e6;
  --ink-100: #f4eff2;
  --rose-600: #a82b44;
  --rose-500: #c93b57;
  --rose-400: #e14d68;
  --rose-100: #fbe0e4;
  --rose-50: #fdf1f0;
  --gold: #c9a227;
  --paper: #fbf7f4;

  display: flex;
  flex-direction: column;
  background: var(--paper);

  .drawer-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .drawer-head {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 22px;
    background: #fff;
    border-bottom: 1px solid var(--ink-200);
  }

  .dh-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .dh-id {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink-900);
    letter-spacing: 0.3px;
  }

  .dh-close {
    appearance: none;
    border: 0;
    background: transparent;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    color: var(--ink-400);
    font-size: 16px;
    cursor: pointer;
    transition: all 0.16s;

    &:hover {
      background: var(--ink-100);
      color: var(--ink-900);
    }
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 20px;
    background: var(--ink-100);
    color: var(--ink-500);

    i {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--ink-300);
    }

    &.s0 {
      background: #fdf4e3;
      color: #8e5b12;
      i { background: #c98a1f; }
    }

    &.s1 {
      background: #eef6f1;
      color: #2f5f47;
      i { background: #3f7a5e; }
    }

    &.s2 {
      background: var(--rose-50);
      color: var(--rose-600);
      i { background: var(--rose-500); }
    }
  }

  .drawer-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 20px 22px 24px;
  }

  /* 纸条本体 —— 详情页的视觉重心 */
  .paper {
    position: relative;
    background: #fffdfb;
    border: 1px solid var(--ink-200);
    border-radius: 4px;
    padding: 26px 26px 22px;
    box-shadow: 0 6px 20px rgba(23, 18, 25, 0.07);
    overflow: hidden;
  }

  .paper-fold {
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 30px;
    background: linear-gradient(225deg, var(--paper) 50%, var(--ink-200) 50.5%, #fffdfb 52%);
  }

  .paper-text {
    margin: 0;
    font-size: 16px;
    line-height: 2;
    color: var(--ink-900);
    white-space: pre-wrap;
    word-break: break-word;
    font-family: 'Songti SC', 'STSong', 'SimSun', Georgia, serif;
  }

  .paper-sign {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px dashed var(--ink-200);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--ink-500);
  }

  .paper-sign-line {
    width: 18px;
    height: 2px;
    background: var(--rose-400);
    border-radius: 2px;
  }

  .paper-sign-school {
    color: var(--ink-300);

    &::before {
      content: '·';
      margin-right: 6px;
    }
  }

  .paper-contact {
    margin-top: 12px;
    font-size: 13px;
    color: var(--ink-500);

    b {
      color: var(--ink-900);
      font-family: 'SFMono-Regular', Consolas, monospace;
      letter-spacing: 0.5px;
    }
  }

  /* 指标 */
  .metric-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 14px;
  }

  .metric {
    background: #fff;
    border: 1px solid var(--ink-200);
    border-radius: 10px;
    padding: 14px 16px;

    b {
      display: block;
      font-size: 20px;
      font-weight: 600;
      color: var(--ink-900);
      line-height: 1.2;

      em {
        margin-left: 4px;
        font-style: normal;
        font-size: 12px;
        font-weight: 400;
        color: var(--ink-400);
      }
    }

    span {
      display: block;
      margin-top: 5px;
      font-size: 12px;
      color: var(--ink-400);
    }
  }

  /* 分区 */
  .section {
    margin-top: 24px;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-700);
    letter-spacing: 0.5px;
    margin-bottom: 12px;

    .count {
      font-size: 11px;
      font-weight: 400;
      color: var(--ink-400);
      background: var(--ink-100);
      border-radius: 10px;
      padding: 1px 8px;
    }

    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--ink-200);
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: var(--ink-200);
    border: 1px solid var(--ink-200);
    border-radius: 10px;
    overflow: hidden;
  }

  .info-item {
    background: #fff;
    padding: 12px 16px;

    &.is-full {
      grid-column: 1 / -1;
    }

    label {
      display: block;
      font-size: 12px;
      color: var(--ink-400);
      margin-bottom: 5px;
    }

    span {
      font-size: 13px;
      color: var(--ink-900);
      word-break: break-word;

      &.is-muted {
        color: var(--ink-300);
      }
    }
  }

  .review-hint {
    margin-top: 12px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.6;
    color: #8e5b12;
    background: #fdf7e8;
    border-radius: 8px;
  }

  /* 取出记录 */
  .take-list {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 0;
      border-bottom: 1px solid var(--ink-100);

      &:last-child {
        border-bottom: 0;
      }
    }
  }

  .take-dot {
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    margin-top: 6px;
    border-radius: 50%;
    background: var(--rose-400);
    box-shadow: 0 0 0 3px var(--rose-50);
  }

  .take-main {
    display: flex;
    flex: 1;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;

    b {
      font-size: 13px;
      font-weight: 500;
      color: var(--ink-900);
    }
  }

  .take-time {
    font-size: 12px;
    color: var(--ink-400);
  }

  .take-empty,
  .take-loading {
    font-size: 13px;
    color: var(--ink-400);
    padding: 16px 0;
    text-align: center;
    background: #fff;
    border: 1px dashed var(--ink-200);
    border-radius: 10px;
  }

  /* 底部操作条 */
  .drawer-foot {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 22px;
    background: #fff;
    border-top: 1px solid var(--ink-200);

    .foot-spacer {
      flex: 1;
    }

    .ghost-btn {
      color: var(--ink-500);
      border-color: var(--ink-200);

      &:hover {
        color: var(--rose-600);
        border-color: var(--rose-500);
        background: var(--rose-50);
      }
    }

    .danger-btn {
      color: #fff;
      background: var(--rose-600);
      border-color: var(--rose-600);
      padding: 9px 16px;

      &:hover,
      &:focus {
        background: var(--rose-500);
        border-color: var(--rose-500);
        color: #fff;
      }
    }
  }
}
</style>
